/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, {
  useRef,
  useEffect,
  useState,
  useCallback,
  useMemo,
  memo,
  useLayoutEffect,
} from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { CENTER_PANEL_ID } from 'constants/feature/statement-content-constants';
import { FormattedMessage } from 'react-intl';
import Loading from 'components/common/loading-component';
import Tooltip from 'components/common/tool-tip-component';

const POPUP_MENU_CLASS = 'custom-context-menu';
const VIEWPORT_MARGIN = 10; // Safety margin to ensure menu is fully visible

const getContainerElement = (containerId = CENTER_PANEL_ID) => {
  return document.getElementById(containerId);
};

// Debounce utility for performance
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// this component renders a context menu for quick actions on an element and renders the menu items with respective to their position
const QuickActionElementContextMenu = ({
  menuOptions = [],
  position,
  onClose,
  data,
  isOCR = false,
  zoomRatio = 1,
  containerId = CENTER_PANEL_ID,
}) => {
  const popupMenuRef = useRef(null);
  const onCloseRef = useRef(onClose);
  const [openSubmenus, setOpenSubmenus] = useState(new Set([]));
  const [clickedItems, setClickedItems] = useState(new Set());

  // Keep onClose ref up to date so capture-phase listeners always call the latest
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  // Finding the co-ordinates where the clicked element is on the viewport
  const clickedElementLocation = useMemo(() => {
    if (position?.elementId) {
      const clickedElement = document.getElementById(position.elementId);
      if (clickedElement) {
        const elementBox = clickedElement.getBoundingClientRect();
        return {
          elementTop: elementBox.top,
          elementRight: elementBox.right,
          elementBottom: elementBox.bottom,
          elementLeft: elementBox.left,
          elementWidth: elementBox.width,
          elementHeight: elementBox.height,
        };
      }
    }

    // Fallback for OCR positioning if elementId is not available (polygon in pixel coordinates)
    if (isOCR && position?.positionData?.coordinates) {
      const { coordinates } = position.positionData;
      return {
        elementTop: coordinates.top,
        elementRight: coordinates.x3,
        elementBottom: coordinates.y3,
        elementLeft: coordinates.left,
        elementWidth: coordinates.width,
        elementHeight: coordinates.height,
      };
    }

    // Final fallback to provided rect
    return position?.targetRect || null;
  }, [
    isOCR,
    position?.positionData?.coordinates,
    position?.elementId,
    position?.targetRect,
    zoomRatio,
  ]);

  const containerElement = useMemo(
    () => getContainerElement(containerId),
    [containerId],
  );

  // Calculate positioning styles without triggering layout
  const positionStyles = useMemo(() => {
    if (!clickedElementLocation) return {};

    // Pre-calculate common values
    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;

    return {
      clickedElementLocation,
      screenWidth,
      screenHeight,
      containerElement,
      isOCR,
      zoomRatio,
    };
  }, [clickedElementLocation, containerElement, isOCR, zoomRatio]);

  // Use useLayoutEffect for DOM measurements and positioning
  useLayoutEffect(() => {
    const popupMenu = popupMenuRef.current;
    if (!popupMenu || !positionStyles.clickedElementLocation) return;

    // Batch DOM reads
    const menuWidth = popupMenu.offsetWidth;
    const menuHeight = popupMenu.offsetHeight;
    const {
      clickedElementLocation,
      screenWidth,
      screenHeight,
      containerElement,
      isOCR,
      zoomRatio,
    } = positionStyles;

    if (!containerElement) {
      // If we are not inside a container, using the whole screen
      const screenWidth = window.innerWidth;
      const screenHeight = window.innerHeight;

      let menuTop = clickedElementLocation.elementBottom;
      let menuLeft = clickedElementLocation.elementLeft;

      // Make sure menu item/s doesn't go off viewport
      if (menuTop + menuHeight > screenHeight) {
        menuTop = Math.max(0, clickedElementLocation.elementTop - menuHeight);
      }
      if (menuLeft + menuWidth > screenWidth) {
        menuLeft = Math.max(0, clickedElementLocation.elementRight - menuWidth);
      }

      popupMenu.style.left = `${menuLeft}px`;
      popupMenu.style.top = `${menuTop}px`;
      popupMenu.style.opacity = '1';
      return;
    }

    // Make sure the container can hold positioned elements with the viewport
    const containerStyle = window.getComputedStyle(containerElement);
    if (containerStyle.position === 'static') {
      containerElement.style.position = 'relative';
    }

    const containerRect = containerElement.getBoundingClientRect();
    const scrollTop = containerElement.scrollTop;
    const scrollLeft = containerElement.scrollLeft;
    const containerWidth = containerRect.width;
    const containerHeight = containerRect.height;
    let menuLeftPosition, menuTopPosition;

    const elementBottomInContainer =
      clickedElementLocation.elementBottom - containerRect.top + scrollTop;
    const elementRightInContainer =
      clickedElementLocation.elementRight - containerRect.left + scrollLeft;
    const elementLeftInContainer =
      clickedElementLocation.elementLeft - containerRect.left + scrollLeft;
    const elementTopInContainer =
      clickedElementLocation.elementTop - containerRect.top + scrollTop;

    if (isOCR) {
      const visibleRightEdge = scrollLeft + containerWidth;

      menuTopPosition = elementBottomInContainer;
      menuLeftPosition = elementRightInContainer;

      if (menuLeftPosition + menuWidth > visibleRightEdge) {
        menuLeftPosition = Math.max(scrollLeft, menuLeftPosition - menuWidth);
      }
    } else {
      menuTopPosition = elementBottomInContainer;
      menuLeftPosition = elementRightInContainer;

      // Visible boundaries in container coordinate space
      const visibleLeft = scrollLeft;
      const visibleRight = scrollLeft + containerWidth;
      const visibleTop = scrollTop;
      const visibleBottom = scrollTop + containerHeight;

      if (menuLeftPosition + menuWidth > visibleRight) {
        menuLeftPosition = Math.max(visibleLeft, menuLeftPosition - menuWidth);
      }

      if (menuTopPosition + menuHeight > visibleBottom) {
        menuTopPosition = Math.max(
          visibleTop,
          elementTopInContainer - menuHeight,
        );
      }
    }

    // Apply position styles and set zoom data attribute for CSS consistency
    popupMenu.setAttribute('data-zoom-ratio', zoomRatio.toString());

    popupMenu.style.left = `${menuLeftPosition}px`;
    popupMenu.style.top = `${menuTopPosition}px`;
    popupMenu.style.position = 'absolute';
    popupMenu.style.transform = 'none';
    popupMenu.style.transformOrigin = '';

    // Batch style updates to minimize reflows
    const styleUpdates = {
      opacity: '1',
      visibility: 'visible',
    };

    Object.assign(popupMenu.style, styleUpdates);
  }, [positionStyles]);

  // Memoize event handlers to prevent recreation on every render
  const eventHandlers = useMemo(() => {
    const closeIfOutside = (event) => {
      const popup = popupMenuRef.current;
      if (!popup || popup.contains(event.target)) return;

      // Use faster selector check
      if (!event.target.closest('[data-submenu]')) {
        onClose();
      }
    };

    const closeIfEscape = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    const closeIfScroll = debounce(onClose, 50);

    return { closeIfOutside, closeIfEscape, closeIfScroll };
  }, [onClose]);

  // Optimize event listener management
  useEffect(() => {
    const { closeIfOutside, closeIfEscape, closeIfScroll } = eventHandlers;

    document.addEventListener('mousedown', closeIfOutside, { passive: true });
    document.addEventListener('keydown', closeIfEscape, { passive: true });
    window.addEventListener('scroll', closeIfScroll, {
      passive: true,
      capture: true,
    });

    return () => {
      document.removeEventListener('mousedown', closeIfOutside);
      document.removeEventListener('keydown', closeIfEscape);
      window.removeEventListener('scroll', closeIfScroll, true);
    };
  }, [eventHandlers]);

  // Capture-phase click listener to close menu when clicking on elements.
  // Uses a ref for onClose so the listener is registered once and always
  // calls the latest callback, avoiding re-registration flicker.
  useEffect(() => {
    const handleClickCapture = (event) => {
      const popup = popupMenuRef.current;
      if (!popup) return;
      if (popup.contains(event.target)) return;
      if (event.target.closest('[data-submenu]')) return;
      onCloseRef.current();
    };

    document.addEventListener('click', handleClickCapture, { capture: true });

    return () => {
      document.removeEventListener('click', handleClickCapture, {
        capture: true,
      });
    };
  }, []);

  useEffect(() => {
    const openClickedItemsWithNewChildren = (items) => {
      items.forEach((item) => {
        // If this item was clicked AND now has children AND submenu is not open
        if (
          clickedItems.has(item.id) &&
          item.children?.length > 0 &&
          !openSubmenus.has(item.id)
        ) {
          // Open the submenu
          setOpenSubmenus((prev) => new Set([...prev, item.id]));

          // Remove from clicked items to prevent re-opening
          setClickedItems((prev) => {
            const next = new Set(prev);
            next.delete(item.id);
            return next;
          });
        }

        // Recursively check children
        if (item.children?.length > 0) {
          openClickedItemsWithNewChildren(item.children);
        }
      });
    };

    openClickedItemsWithNewChildren(menuOptions);
  }, [menuOptions, clickedItems, openSubmenus]);

  // Optimize submenu toggle with early return for unchanged state
  const toggleSubmenu = useCallback((itemId, shouldOpen) => {
    setOpenSubmenus((prevSubmenus) => {
      const isCurrentlyOpen = prevSubmenus.has(itemId);
      if (isCurrentlyOpen === shouldOpen) return prevSubmenus; // Early return for no change

      const nextSubmenus = new Set(prevSubmenus);
      if (shouldOpen) {
        nextSubmenus.add(itemId);
      } else {
        nextSubmenus.delete(itemId);
      }
      return nextSubmenus;
    });
  }, []);

  // when user clicks on a menu/submenu item
  const whenClicked = useCallback(
    (menuItem, event) => {
      event.stopPropagation();
      setClickedItems((prev) => new Set([...prev, menuItem.id]));

      if (menuItem.children?.length > 0) {
        // If the current menu item has children, open/close them better
        toggleSubmenu(menuItem.id, !openSubmenus.has(menuItem.id));
      } else {
        if (menuItem.type === 'separator') {
          return; // Do nothing on separator click
        }
        // If no children, do the action and close menu
        onClose(data, menuItem, event);
      }
    },
    [toggleSubmenu, openSubmenus, onClose, data],
  );

  const whenMouseOver = useCallback(
    (menuItem, event) => {
      event.stopPropagation();

      if (menuItem.children?.length > 0) {
        // Open this submenu
        toggleSubmenu(menuItem.id, true);

        // ✅ Close sibling submenus at the same level
        setOpenSubmenus((prevSubmenus) => {
          const nextSubmenus = new Set(prevSubmenus);

          // Helper function to find siblings and close them
          const closeSiblingsAndTheirChildren = (
            items,
            targetId,
            parentId = null,
          ) => {
            for (let i = 0; i < items.length; i++) {
              const item = items[i];

              // If we found our target item, close all its siblings
              if (item.id === targetId) {
                // Close all other items at this level (siblings)
                items.forEach((sibling) => {
                  if (sibling.id !== targetId && sibling.children?.length > 0) {
                    nextSubmenus.delete(sibling.id);
                    // Recursively close all children of siblings
                    const closeAllChildren = (parentItem) => {
                      if (parentItem.children) {
                        parentItem.children.forEach((child) => {
                          nextSubmenus.delete(child.id);
                          closeAllChildren(child);
                        });
                      }
                    };
                    closeAllChildren(sibling);
                  }
                });
                return true;
              }

              // Recursively search in children
              if (item.children?.length > 0) {
                const found = closeSiblingsAndTheirChildren(
                  item.children,
                  targetId,
                  item.id,
                );
                if (found) return true;
              }
            }
            return false;
          };

          closeSiblingsAndTheirChildren(menuOptions, menuItem.id);

          // Keep the target submenu open
          nextSubmenus.add(menuItem.id);

          return nextSubmenus;
        });
      } else {
        // ✅ If it's a leaf item (no children), close all its siblings' submenus
        setOpenSubmenus((prevSubmenus) => {
          const nextSubmenus = new Set(prevSubmenus);

          // Find and close sibling submenus
          const closeSiblingSubmenus = (items, targetId) => {
            for (let i = 0; i < items.length; i++) {
              const item = items[i];

              if (item.id === targetId) {
                // Found target, close all sibling submenus
                items.forEach((sibling) => {
                  if (sibling.id !== targetId && sibling.children?.length > 0) {
                    nextSubmenus.delete(sibling.id);
                    // Close all descendants
                    const closeDescendants = (parentItem) => {
                      if (parentItem.children) {
                        parentItem.children.forEach((child) => {
                          nextSubmenus.delete(child.id);
                          closeDescendants(child);
                        });
                      }
                    };
                    closeDescendants(sibling);
                  }
                });
                return true;
              }

              if (item.children?.length > 0) {
                const found = closeSiblingSubmenus(item.children, targetId);
                if (found) return true;
              }
            }
            return false;
          };

          closeSiblingSubmenus(menuOptions, menuItem.id);

          return nextSubmenus;
        });
      }
    },
    [toggleSubmenu, menuOptions],
  );

  const whenMouseLeaves = useCallback((menuItem, event) => {
    event.stopPropagation();

    if (menuItem.children?.length > 0) {
      const relatedTarget = event.relatedTarget;

      if (relatedTarget && relatedTarget instanceof Node) {
        //Check if moving to the submenu
        const submenuElement = document.querySelector(
          `[data-submenu="${menuItem.id}"]`,
        );
        if (submenuElement?.contains(relatedTarget)) {
          return; // Stay open - moving to submenu
        }

        //Check if moving back to parent menu item wrapper
        const parentWrapper = document.querySelector(
          `[data-menu-item="${menuItem.id}"]`,
        );
        if (parentWrapper?.contains(relatedTarget)) {
          return; // Stay open - moving within the same menu item
        }

        //Check if moving to any child submenu (nested)
        const allSubmenus = document.querySelectorAll('[data-submenu]');
        const isMovingToChildSubmenu = Array.from(allSubmenus).some(
          (submenu) => {
            const isChild = submenuElement?.contains(submenu);
            const containsTarget = submenu.contains(relatedTarget);
            return isChild && containsTarget;
          },
        );

        if (isMovingToChildSubmenu) {
          return; // Stay open - moving to a nested child submenu
        }

        //Check if moving to parent hierarchy
        const allParentWrappers = document.querySelectorAll('[data-menu-item]');
        const isMovingToParent = Array.from(allParentWrappers).some(
          (wrapper) => {
            //Check if this wrapper is a parent of current item
            const wrapperId = wrapper.getAttribute('data-menu-item');
            const wrapperSubmenu = document.querySelector(
              `[data-submenu="${wrapperId}"]`,
            );
            const isParent = wrapperSubmenu?.contains(parentWrapper);
            const containsTarget = wrapper.contains(relatedTarget);
            return isParent && containsTarget;
          },
        );

        if (isMovingToParent) {
          return; // Stay open - moving to parent menu
        }
      }
    }
  }, []);

  // Draw each menu item on screen
  const drawMenuItem = useCallback(
    (menuItem, indentLevel = 0) => {
      // Draw a line to separate menu items
      if (menuItem.type === 'separator') {
        return (
          <div
            key={menuItem.id || `separator-${Math.random()}`}
            className={`${POPUP_MENU_CLASS}-separator`}
          />
        );
      }
      if (menuItem.type === 'loader') {
        return <Loading />;
      }

      const hasChildren = menuItem.children?.length > 0;
      const isSubmenuOpen = openSubmenus.has(menuItem.id);
      const isDisabled = menuItem.disabled;
      let menuLabel = menuItem.isNotInternationalized ? (
        <span className={`${POPUP_MENU_CLASS}--item-label`}>
          {menuItem.label}
        </span>
      ) : (
        <span className={`${POPUP_MENU_CLASS}--item-label`}>
          <FormattedMessage id={menuItem.label} />
        </span>
      );

      if (menuItem.tooltipConfig) {
        menuLabel = <Tooltip {...menuItem.tooltipConfig}>{menuLabel}</Tooltip>;
      }

      return (
        <div
          key={menuItem.id}
          className={`${POPUP_MENU_CLASS}--item-wrapper`}
          style={{ position: 'relative' }}
          data-menu-item={menuItem.id}
          onMouseEnter={(event) => whenMouseOver(menuItem, event)}
          onMouseLeave={(event) => whenMouseLeaves(menuItem, event)}
        >
          <div
            className={classNames(`${POPUP_MENU_CLASS}--item`, {
              [`${POPUP_MENU_CLASS}--item--parent`]: hasChildren, // has submenu
              [`${POPUP_MENU_CLASS}--item--disabled`]: isDisabled, // disabled menu/submenu
              [`${POPUP_MENU_CLASS}--item--expanded`]: isSubmenuOpen, // submenu open
            })}
            style={{
              cursor: isDisabled ? 'not-allowed' : 'pointer',
            }}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(clickEvent) => {
              !isDisabled && whenClicked(menuItem, clickEvent);
            }}
            role="menuitem"
            tabIndex={isDisabled ? -1 : 0}
          >
            {menuLabel}
            {hasChildren && (
              <span className={`${POPUP_MENU_CLASS}--item-arrow`}>▶</span>
            )}
          </div>
          {hasChildren &&
            isSubmenuOpen &&
            (() => {
              const submenuContainer = document.getElementById(containerId);
              if (!submenuContainer) return null;

              return ReactDOM.createPortal(
                <div
                  className={POPUP_MENU_CLASS}
                  data-submenu={menuItem.id}
                  data-zoom-ratio={zoomRatio}
                  onMouseEnter={() => toggleSubmenu(menuItem.id, true)}
                  onMouseLeave={() => toggleSubmenu(menuItem.id, false)}
                  ref={(submenuRef) => {
                    if (!submenuRef) return;

                    const container = document.getElementById(containerId);
                    if (!container) return;

                    const parentMenuItem = document.querySelector(
                      `[data-menu-item="${menuItem.id}"] .${POPUP_MENU_CLASS}--item`,
                    );
                    if (!parentMenuItem) return;

                    // Set zoom ratio attribute for CSS consistency
                    submenuRef.setAttribute(
                      'data-zoom-ratio',
                      zoomRatio.toString(),
                    );

                    const parentRect = parentMenuItem.getBoundingClientRect();
                    const containerRect = container.getBoundingClientRect();
                    const scrollTop = container.scrollTop;
                    const scrollLeft = container.scrollLeft;
                    const submenuWidth = submenuRef.offsetWidth || 150;
                    const submenuHeight = submenuRef.offsetHeight;
                    const parentLeftPosition =
                      parentRect.left - containerRect.left + scrollLeft;
                    const parentRightPosition =
                      parentRect.right - containerRect.left + scrollLeft;
                    const parentTopPosition =
                      parentRect.top - containerRect.top + scrollTop;
                    const containerWidth = containerRect.width;
                    const containerHeight = containerRect.height;
                    const visibleRightEdge = scrollLeft + containerWidth;
                    const visibleBottomEdge = scrollTop + containerHeight;
                    const spaceOnRight = visibleRightEdge - parentRightPosition;
                    const spaceOnLeft = parentLeftPosition - scrollLeft;

                    const submenuLeftPosition =
                      spaceOnRight >= submenuWidth + 4
                        ? parentRightPosition + 4
                        : spaceOnLeft >= submenuWidth + 4
                          ? parentLeftPosition - submenuWidth - 4
                          : spaceOnRight > spaceOnLeft
                            ? parentRightPosition + 4
                            : parentLeftPosition - submenuWidth - 4;

                    let submenuTopPosition = parentTopPosition;
                    if (
                      submenuTopPosition + submenuHeight >
                      visibleBottomEdge
                    ) {
                      submenuTopPosition = Math.max(
                        scrollTop,
                        visibleBottomEdge - submenuHeight - 10,
                      );
                    }

                    submenuRef.style.left = `${submenuLeftPosition}px`;
                    submenuRef.style.top = `${submenuTopPosition}px`;
                  }}
                >
                  <div className={`${POPUP_MENU_CLASS}--body`}>
                    {menuItem.children.map((childItem) =>
                      drawMenuItem(childItem, indentLevel + 1),
                    )}
                  </div>
                </div>,
                submenuContainer,
              );
            })()}
        </div>
      );
    },
    [
      openSubmenus,
      whenClicked,
      whenMouseOver,
      whenMouseLeaves,
      toggleSubmenu,
      zoomRatio,
      containerElement,
    ],
  );

  const menuContainer = document.getElementById(containerId);
  if (!menuContainer) return null;

  return ReactDOM.createPortal(
    <div
      ref={popupMenuRef}
      className={POPUP_MENU_CLASS}
      data-ocr-menu={isOCR}
      style={{
        opacity: 0,
        visibility: 'hidden', // Prevent flashing before positioning
        position: 'absolute',
        pointerEvents: 'auto',
        zIndex: 9999,
        willChange: zoomRatio >= 1 ? 'transform' : 'auto',
        contain: 'layout style paint', // CSS containment for better performance
      }}
      role="menu"
      tabIndex={-1}
      onContextMenu={(e) => e.preventDefault()}
    >
      <div className={`${POPUP_MENU_CLASS}--body`}>
        {menuOptions.map((menuItem) => drawMenuItem(menuItem))}
      </div>
    </div>,
    menuContainer,
  );
};

QuickActionElementContextMenu.propTypes = {
  /* quick actions available for the element */
  menuOptions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      label: PropTypes.string,
      type: PropTypes.string,
      disabled: PropTypes.bool,
      children: PropTypes.array,
    }),
  ),
  /* position where the menu should appear */
  position: PropTypes.shape({
    x: PropTypes.number.isRequired,
    y: PropTypes.number.isRequired,
    elementId: PropTypes.string,
    /* legacy elements coordinates for positioning the menu */
    targetRect: PropTypes.shape({
      top: PropTypes.number,
      right: PropTypes.number,
      bottom: PropTypes.number,
      left: PropTypes.number,
      width: PropTypes.number,
      height: PropTypes.number,
    }),
    /* OCR elements coordinates for positioning the menu */
    positionData: PropTypes.shape({
      polygonInPixel: PropTypes.oneOfType([
        /* if element is Single-line: [x1, y1, x2, y2, x3, y3, x4, y4] */
        PropTypes.array,
        /* if element is Multi-line: [[...], [...]] */
        PropTypes.arrayOf(PropTypes.array),
      ]),
      boundary: PropTypes.object,
      isMultiLine: PropTypes.bool,
      coordinates: PropTypes.shape({
        top: PropTypes.number,
        left: PropTypes.number,
        width: PropTypes.number,
        height: PropTypes.number,
        x1: PropTypes.number,
        y1: PropTypes.number,
        x2: PropTypes.number,
        y2: PropTypes.number,
        x3: PropTypes.number,
        y3: PropTypes.number,
        x4: PropTypes.number,
        y4: PropTypes.number,
      }),
    }),
  }),
  /* function to call when closing/onclicking the menu */
  onClose: PropTypes.func,
  /* element data which the menu is for */
  data: PropTypes.object,
  /*  OCR element or regular element */
  isOCR: PropTypes.bool,
  /* zoom ratio for scaling the menu */
  zoomRatio: PropTypes.number,
};

export default memo(QuickActionElementContextMenu);
